//@flow
import Authenticator from '../protocol/Authenticator';
import HttpOperation from '../protocol/HttpOperation';
import ErrorId from '../protocol/ErrorId';
import AuthTypeHandler from './AuthTypeHandler';
import type { AuthType } from './AuthType';
import Store from '../store/Store';
import StringUtil from '../util/StringUtil';
import AuthMechanism, { NULL_BASIC_REALM, NULL_OAUTH_REALM, AuthRealm } from './AuthMechanism';
import Storage from '../protocol/Storage';

const debug = require('../debug/debug')('wc:auth:Authenticator2');

export default class Authenticator2 extends Authenticator {
  static AUTH_STORE_NAME: string = 'AUTH_STORE';
  static AUTH_STORE: Store = null;
  static AUTH_TYPE: string = 'AUTH_TYPE';
  /**
   * Constant for storing to getting the array of auth mechanisms.
   * @static
   * @type {string}
   * @memberof Authenticator2
   */
  static AUTH_TYPES: string = 'AUTH_TYPES';
  static GLOBAL_LOGOUT_URL: string = 'GLOBAL_LOGOUT_URL';
  static OAUTH_RESPONSE_DATA_EVENT: string = 'OAUTH_RESPONSE_DATA';

  constructor() {
    super();
    if (debug.enabled) {
      debug('[constructor()] new initialized');
    }
    //Note: Not initialized in the declaration above, it causes some problem in bootstrapping of the Webclient.
    if (Authenticator2.AUTH_STORE == null) {
      Authenticator2.AUTH_STORE = new Store(Authenticator2.AUTH_STORE_NAME);
    }
  }

  /**
   * This method we override from old authenticator
   * There we detect if ping is enabled or not.
   * @param {HttpOperation} httpOperation
   * @param {number} httpStatusCode
   * @param {Object} jsonData
   * @param {XMLHttpRequest} jqrXhr
   */
  onAuthFailure(httpOperation: HttpOperation, httpStatusCode: number, jsonData: any, jqrXhr: XMLHttpRequest): ErrorId {
    if (debug.enabled) {
      debug('[onAuthFailure(%s, %d, %s)]', httpOperation.name, httpStatusCode, jsonData);
    }
    try {
      const errorId = this.getErrorId(
        httpStatusCode,
        jsonData instanceof Object || jqrXhr == null || !jqrXhr.hasOwnProperty('responseJSON')
          ? jsonData
          : jqrXhr.responseJSON
      );
      if (!Authenticator.isLoginQuery(httpOperation) && !Authenticator.isGetCredentialsQuery(httpOperation)) {
        this.storeFailedRequest(httpOperation);
      }

      if (debug.enabled) {
        debug('[onAuthFailure()] Storing auth type in store. Which will be used for login later.');
      }
      Authenticator2.setAuthTypes(jqrXhr);

      if (debug.enabled) {
        debug('Auth types: %s', JSON.stringify(Authenticator2.getAuthTypes()));
      }
      this.invokeOnAuthFailureCallback(errorId);

      return errorId;
    } catch (error) {
      //We must have received the invalid auth header. Proceed the old way.
      if (debug.enabled) {
        debug('[onAuthFailure()] Error in auth header. Error: %s', error.toString());
      }
      return super.onAuthFailure(httpOperation, httpStatusCode, jsonData, jqrXhr);
    }
  }

  /**
   * @param httpStatusCode{number}
   * @param data{*}
   * @return {ErrorId}
   */
  getErrorId(httpStatusCode, data) {
    const parsedError = this.parseError(data);
    const backendErrorKey = parsedError == null ? null : parsedError.key;
    const backendErrorDetails = parsedError == null ? null : parsedError.msg;
    const errorKey = this.getErrorKey(httpStatusCode);
    let errorId = null;
    if (errorKey) {
      const error = this.getError(httpStatusCode, backendErrorDetails);
      errorId = new ErrorId(httpStatusCode, error, errorKey, backendErrorDetails);
    } else {
      const error = backendErrorKey && backendErrorKey !== 'UNKNOWN_ERROR' ? t(backendErrorKey) : null;
      if (error) {
        errorId = new ErrorId(httpStatusCode, error, backendErrorKey, backendErrorDetails);
      } else {
        /* TODO: Re-work this block along with the error message displaying framework, since the error message is not localized.
              - Perhaps, the message displaying framework could show the generic 'UNKNOWN_ERROR' or another context-aware generic message;
                with the ErrorId.backendError visible via hover or a "Details" button.
              - Alternatively, add more support for known keys in the backend such as LoginStatusEnum/ErrorCode, as well as, in the frontend.
                  - However, this will require sending the error params to the frontend too, which is complicated.
         */
        errorId = new ErrorId(
          httpStatusCode,
          backendErrorDetails ? backendErrorDetails : null,
          backendErrorKey ? backendErrorKey : 'UNKNOWN_ERROR'
        );
      }
    }
    return errorId;
  }

  static isOAuthMechanismAvailable(): boolean {
    return this.findOAuthType().length > 0;
  }

  static isBasicMechanismAvailable(): boolean {
    return this.findBasicAuthType().length > 0;
  }

  static findOAuthType(): Array<AuthType> {
    return this.getAuthTypeForAuthMechanism(AuthMechanism.OAuth);
  }

  static findBasicAuthType(): Array<AuthType> {
    return this.getAuthTypeForAuthMechanism(AuthMechanism.Basic);
  }

  static isNullTypeRealm(authRealm: AuthRealm): boolean {
    return authRealm === NULL_BASIC_REALM || authRealm === NULL_OAUTH_REALM;
  }

  static realmName(authRealm: AuthRealm): ?string {
    return this.isNullTypeRealm(authRealm) ? undefined : authRealm.name;
  }

  /**
   *
   * This method looks for the passed argumets by the JSP to the window object.<br>
   * 1. We pass the argument: window.externalUserAuthMechanism from the index.jsp.
   * 2. We pass the argument: window.externalUserAuthRealm from the index.jsp.
   * These two arguments received in the EntryPointJsp.java which is called from index.jsp.
   * We need these arguments, so, we don't hard core the External user realm and mechanism in the frontend code.
   * This way, backend can enforce the whatever realm for the external user.
   * window.externalUserAuthRealm
   * @static
   * @returns {?AuthRealm}
   * @memberof Authenticator2
   */
  static getAuthRealmForExternalUserFromWindowsArgument(): ?AuthRealm {
    if (window) {
      if (window.externalUserAuthRealm && window.externalUserAuthMechanism) {
        if (debug.enabled) {
          debug(
            'Received options: window.externalUserAuthMechanism = %s and window.externalUserAuthRealm = %s',
            window.externalUserAuthMechanism,
            window.externalUserAuthRealm
          );
        }
        let authMechanism = AuthMechanism.toEnum(window.externalUserAuthMechanism);
        return new AuthRealm(window.externalUserAuthRealm, authMechanism ? authMechanism : AuthMechanism.Basic);
      }
    }
    return null;
  }

  static getAuthTypeForAuthMechanism(authMechanism: AuthMechanism): Array<AuthType> {
    const matches = [];
    const authTypes: ?Array<AuthType> = Authenticator2.AUTH_STORE.get(Authenticator2.AUTH_TYPES);
    if (authTypes && authTypes.length > 0) {
      authTypes.forEach((authType) => {
        if (authType.getAuthRealm().authMechanism === authMechanism) {
          matches.push(authType);
        }
      });
    }
    return matches;
  }

  static isAuthTypeForAuthMechanism(authMechanism: AuthMechanism): boolean {
    const authTypes: ?Array<AuthType> = Authenticator2.AUTH_STORE.get(Authenticator2.AUTH_TYPES);
    if (authTypes && authTypes.length > 0) {
      for (var i = 0, l = authTypes.length; i < l; i++) {
        const authType: AuthType = authTypes[i];
        if (authType.getAuthRealm().authMechanism === authMechanism) {
          return true;
        }
      }
    }
    return false;
  }

  static getAuthTypes(): ?Array<AuthType> {
    return Authenticator2.AUTH_STORE.get(Authenticator2.AUTH_TYPES);
  }

  static setAuthTypes(jqrXhr: XMLHttpRequest): void {
    const authTypes: Array<AuthType> = AuthTypeHandler.getAuthTypes(jqrXhr);
    if (authTypes.length > 0 && Authenticator2.AUTH_STORE != null) {
      Authenticator2.AUTH_STORE.put(Authenticator2.AUTH_TYPES, authTypes);
    }
  }

  static setAuthTypesFromFetchHeaders(headers: Headers): void {
    const authTypes: Array<AuthType> = AuthTypeHandler.getAuthTypesFromFetchHeaders(headers);
    if (authTypes.length > 0 && Authenticator2.AUTH_STORE != null) {
      Authenticator2.AUTH_STORE.put(Authenticator2.AUTH_TYPES, authTypes);
    }
  }
}
